import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { IEntiteEditComponent } from 'src/app/core/interface/ientite-edit-component';
import { Dico } from 'src/app/core/models/dico';
import { EntiteEditor } from 'src/app/core/models/entite-editor';
import { EntiteSearchor } from 'src/app/core/models/entite-searchor';
import { IdCodeLabel } from 'src/app/core/models/IdCodeLabel';
import { SearchParam } from 'src/app/core/models/search-param';
import { ILoutyEntiteService } from 'src/app/core/services/ilouty-entite-service';
import { ToastMessagesService } from 'src/app/core/services/toast-messages.service';
import { MenuContexteService } from 'src/app/layout/services/menu-contexte.service';
import { MembreService } from 'src/app/membres/services/membre.service';
import { AssociationMembreActiviteDto, MembreActivitesDto } from '../../models/association-membre-activite-dto';
import { AssociationMembreActivitesService } from '../../services/association-membre-activites.service';
import { AssociationMembreActivitesPopupComponent } from '../association-membre-activites-popup/association-membre-activites-popup.component';
import { MEMBRE_ACTIVITES_EDITOR } from 'src/app/core/const/injection-tokens';

@Component({
  selector: 'app-association-membre-activites',
  templateUrl: './association-membre-activites.component.html',
  styleUrls: ['./association-membre-activites.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AssociationMembreActivitesComponent implements OnInit, OnChanges, IEntiteEditComponent<MembreActivitesDto> {
  constructor(
    private associationMembreActivitesService: AssociationMembreActivitesService,
    private membreService: MembreService,
    private toastMessagesService: ToastMessagesService,
    private fb: FormBuilder,
    public route: ActivatedRoute,
    public router: Router,
    // public : le gabarit lit editor.erreursFiche (bandeau, #1083).
    @Inject(MEMBRE_ACTIVITES_EDITOR) public editor: EntiteEditor<MembreActivitesDto>,
    private menuContexteService: MenuContexteService,
  ) {
    this.membreSearchor = new EntiteSearchor((searchParam: SearchParam<Dico<any>>) => this.membreService.quickSearch(searchParam), {
      masque: false,
    });
  }
  downloading: boolean;
  exporting: boolean;
  pdfDispo: boolean;
  label: string;
  forcingKeys: string[];
  reloadAfterSave = true;
  getSaveConfirmMsg?: (dto: MembreActivitesDto) => string;
  resetAvantPatchValue?: (dto: MembreActivitesDto) => void;
  resetApresPatchValue?: (dto: MembreActivitesDto) => void;

  listenChanges(): void {}

  get activiteDeal() {
    return this.associationMembreActivitesService.getDefaultActiviteDeal();
  }

  doAfterSave(_id: number): Promise<void> {
    return new Promise<void>((resolve, _reject) => {
      resolve();
    });
  }

  save() {
    this.editor.save(this.form.getRawValue());
  }

  ignoreCreation?: boolean;
  warnOnReset?: (dto: MembreActivitesDto) => string;
  entiteService: ILoutyEntiteService<MembreActivitesDto, any> = this.associationMembreActivitesService;
  form: FormGroup;
  @Input() readonly = false;
  saving: boolean;
  reload: () => void;
  consigne?: (control: FormControl) => void;
  @Input() membreId;
  // @Input() membre: IdCodeLabel;
  membreSearchor: EntiteSearchor<IdCodeLabel>;
  getBreadcrumbLabel(): string {
    return '';
  }
  // static : l'en-tête lit le menu du popup dès son premier rendu (#1083).
  @ViewChild(AssociationMembreActivitesPopupComponent, { static: true })
  popupBarAssociationMembreActivites: AssociationMembreActivitesPopupComponent;

  buildEditForm(): void {
    this.form = this.fb.group({
      id: [this.membreId, Validators.required],
      membreActiviteList: [],
    });
  }

  ngOnInit(): void {
    this.editor.init(this);
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (!this.membreSearchor) {
      this.membreSearchor = new EntiteSearchor((searchParam: SearchParam<Dico<any>>) => this.membreService.quickSearch(searchParam), {
        masque: false,
      });
    }
    if (simpleChanges['membreId']) {
      if (this.membreId) {
        this.form?.get('id').setValue(this.membreId);
        this.membreSearchor.searchById(this.membreId).then(() => this.membreSearchor.initSearchParam());
      }
      // Partie commune au changement depuis l'input ou depuis l'interface (form)
      this.afterMembreChange();
    }
  }

  onMembreChange() {
    this.membreId = this.form?.get('id').value;
    this.afterMembreChange();
  }

  afterMembreChange() {
    if (this.membreId) {
      this.associationMembreActivitesService.get(this.membreId).subscribe((dto) => {
        this.form.patchValue(dto);
        this.form.markAsPristine();
      });
      this.menuContexteService.setContexte([
        {
          codeInterface: 'ME_ACT_MB',
          codeContexte: 'MEMBRE',
          valeur: this.membreId,
        },
      ]);
    } else {
      this.form?.reset();
      this.menuContexteService.clearContexte();
    }
  }

  alertAssoNonSupprimable() {
    this.toastMessagesService.showWarning(
      "Cette association n'est pas supprimable, car des notes de frais concernent ce membre et cette activité. Vous pouvez cependant lui affecter une date de fin.",
    );
  }

  // assoNonSupprimable = (ctrl: FormGroup): boolean => !!ctrl.value['nfAssociees']
  assoNonSupprimable = (_ctrl: FormGroup): boolean => false;

  resumeAsso = (a: AssociationMembreActiviteDto) => a.activite?.label ?? 'nouvelle';

  get newAssoDto() {
    // Definition de l'activite importante pour la methos onSaisieActivite du popup
    return new AssociationMembreActiviteDto({ activite: new IdCodeLabel({ id: null }) });
  }

  get activiteAssocieeList() {
    return this.form
      .get('membreActiviteList')
      .value?.filter((v) => v.activite?.id)
      .map((v) => v.activite.id);
  }

  get membreActivites() {
    return this.form.get('membreActiviteList').value ?? [];
  }
}
